import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    Grid,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    Typography,
    makeStyles,
} from '@material-ui/core';
import * as actions from '../store/actions';
import GraphicsLibrary from '../components/GraphicsLibrary/GraphicsLibrary';
import CloseIcon from '@material-ui/icons/Close';
import GraphicUploadContainer from './GraphicUploadContainer';
import { useSnackbar } from 'notistack';
import { useTypedSelector } from '../shared/hooks/useTypedSelector';
import { ISnackbarNotification } from '../objects/app/snackbar';
import { ITheme } from '../themes/custom/ITheme';
import BackToLibraryDialog from '../components/BackToLibraryDialog/BackToLibraryDialog';
import { useTranslation } from 'react-i18next';
import KapinuaDialogTitle from '../components/KapinuaDialog/KapinuaDialogTitle';

const useStyles = makeStyles((theme: ITheme) => {
    return {
        titleButton: {
            height: '30px',
        },
        closeButton: {
            color: theme.custom.palette.secondary,
        },
        dialogTitle: {
            // background: theme.custom.palette.dialogTitleBackground,
        },
    };
});

interface Props {
    onInsertGraphicFuncName: string;
    currentDesignId: string;
    customerId: number | null;
    guestId: number | null;
}

declare global {
    interface Window {
        openGraphicsLibrary: any;
        closeGraphicsLibrary: any;
        getGraphicIdsFromCurrentDesign: any;
    }
}

const GraphicsLibraryContainer: React.FC<Props> = (props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [notificationsDisplayed, setNotificationsDisplayed] = React.useState<
        number[]
    >([]);
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const classes = useStyles();
    const { fileCount } = useTypedSelector(
        (state) => state.graphicUpload.submit
    );

    const { onInsertGraphicFuncName, currentDesignId, customerId, guestId } =
        props;

    const { notifications } = useTypedSelector(
        (state) => state.snackbar.notifications
    );

    const handleOnClose = () => {
        setIsOpen(false);
    };

    const showGraphicUpload = () => {
        setIsUploadOpen(true);
    };

    const onGraphicUpload = () => {
        dispatch(
            actions.setGraphicsFilter([
                {
                    key: 'myGraphics',
                    value: true,
                },
            ])
        );
        showLibrary();
    };

    const [backToLibraryDialogOpen, setBackToLibraryDialogOpen] =
        React.useState(false);

    const handleBackToLibraryDialogClose = () => {
        setBackToLibraryDialogOpen(false);
    };

    const handleBackToLibraryDialogAgree = () => {
        dispatch(actions.graphicUploadSubmitReset());
        setBackToLibraryDialogOpen(false);
        setIsUploadOpen(false);
    };

    const showLibrary = () => {
        if (fileCount > 0) {
            setBackToLibraryDialogOpen(true);
        } else {
            setIsUploadOpen(false);
        }
    };

    useEffect(() => {
        window.openGraphicsLibrary = () => {
            setIsOpen(true);
        };
        window.closeGraphicsLibrary = () => {
            setIsOpen(false);
        };

        return () => {};
    }, []);

    useEffect(() => {
        notifications.forEach((notification: ISnackbarNotification) => {
            if (!notificationsDisplayed.includes(notification.key)) {
                enqueueSnackbar(notification.message, {
                    variant: notification.type,
                    preventDuplicate: true,
                });
                setNotificationsDisplayed([
                    ...notificationsDisplayed,
                    notification.key,
                ]);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications]);

    return (
        <>
            <BackToLibraryDialog
                open={backToLibraryDialogOpen}
                handleOnAgree={handleBackToLibraryDialogAgree}
                handleOnClose={handleBackToLibraryDialogClose}
            />
            <Dialog
                fullWidth={true}
                onClose={handleOnClose}
                open={isOpen}
                maxWidth={'xl'}
            >
                <KapinuaDialogTitle
                    handleOnClose={handleOnClose}
                    overrideChildren={true}
                >
                    <Grid
                        justifyContent="space-between"
                        alignItems="center"
                        container
                    >
                        <Grid item>
                            <Grid container spacing={3} alignItems="center">
                                <Grid item>
                                    <Typography variant="h6">
                                        {isUploadOpen && t('Graphics Upload')}
                                        {!isUploadOpen && t('Graphic library')}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    {isUploadOpen && (
                                        <Button
                                            onClick={showLibrary}
                                            color="secondary"
                                            variant="outlined"
                                            className={classes.titleButton}
                                        >
                                            {t('Back to library')}
                                        </Button>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <IconButton
                                onClick={handleOnClose}
                                color="secondary"
                            >
                                <CloseIcon className={classes.closeButton} />
                            </IconButton>
                        </Grid>
                    </Grid>
                </KapinuaDialogTitle>
                <DialogContent>
                    <Box>
                        {isUploadOpen && (
                            <GraphicUploadContainer
                                onSubmit={onGraphicUpload}
                                customerId={customerId}
                                guestId={guestId}
                            />
                        )}
                        {!isUploadOpen && (
                            <GraphicsLibrary
                                currentDesignId={currentDesignId}
                                onInsertGraphicFuncName={
                                    onInsertGraphicFuncName
                                }
                                customerId={customerId}
                                guestId={guestId}
                                showGraphicUpload={showGraphicUpload}
                            />
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default GraphicsLibraryContainer;
